import React, { useState, useEffect } from 'react';
import { FaSync, FaChartLine, FaExclamationTriangle, FaRobot, FaArrowUp, FaArrowDown, FaBrain, FaShieldAlt, FaLightbulb } from 'react-icons/fa';
import axios from 'axios';
import MarketNewsTicker from '../components/MarketNewsTicker';
import aliceBlueWS from '../services/aliceBlueWebSocket';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const MarketSentimentPage = () => {
  const [selectedSymbol, setSelectedSymbol] = useState('NIFTY');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [briefingData, setBriefingData] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [newsSentiment, setNewsSentiment] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [livePrices, setLivePrices] = useState({});
  const [indexPrices, setIndexPrices] = useState({});

  const symbols = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'SENSEX'];
  
  // State to store current month futures tokens (will be fetched dynamically)
  const [futuresTokens, setFuturesTokens] = useState({});
  
  // Indices data with their instrument tokens for WebSocket
  // These are INDEX tokens - not used for spot price
  const indicesData = {
    'NIFTY': { exchange: 'NSE', token: '26000' },
    'BANKNIFTY': { exchange: 'NSE', token: '26009' },
    'FINNIFTY': { exchange: 'NSE', token: '26037' },
    'SENSEX': { exchange: 'BSE', token: '1' }
  };

  // Handle news sentiment updates
  const handleNewsUpdate = (sentiment) => {
    setNewsSentiment(sentiment);
  };

  // Fetch index prices from backend (REST API fallback for WebSocket)
  const fetchIndexPrices = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/india/index-prices`);
      if (response.data.success && response.data.data) {
        setIndexPrices(response.data.data);
        console.log('📊 Loaded index prices from backend:', response.data.data);
      }
    } catch (error) {
      console.error('Error fetching index prices:', error);
    }
  };

  // Fetch current month futures contract for spot price
  const fetchCurrentMonthFutures = async () => {
    try {
      // Fetch current month futures from backend API
      const response = await axios.get(`${API_BASE_URL}/api/futures/current-month`);

      if (response.data.success) {
        const futuresData = response.data.data;
        console.log('📊 Loaded current month futures:', Object.keys(futuresData).join(', '));
        console.log('📊 Futures tokens:', futuresData);
        setFuturesTokens(futuresData);
        return futuresData;
      } else {
        console.error('Failed to fetch futures:', response.data.error);
        setFuturesTokens({});
        return {};
      }
    } catch (error) {
      console.error('Error fetching futures contracts:', error);
      setFuturesTokens({});
      return {};
    }
  };

  // Initialize WebSocket connection and fetch futures
  useEffect(() => {
    let mounted = true;

    const initWebSocket = async () => {
      try {
        await aliceBlueWS.connect();
        if (!mounted) return;
        
        setWsConnected(true);
        console.log('✅ [Market Sentiment] WebSocket connected');

        // Subscribe to WebSocket price updates
        const priceCallback = (data) => {
          const key = `${data.exchange}|${data.token}`;
          console.log(`📊 Price update for ${key}:`, { lp: data.lp, ltp: data.ltp });
          
          setLivePrices(prev => {
            return {
              ...prev,
              [key]: {
                ...prev[key],
                ...data,
                ltp: data.lp || data.ltp || prev[key]?.ltp,
                change: data.cv || data.change || prev[key]?.change,
                changePercent: data.changePercent || prev[key]?.changePercent
              }
            };
          });
        };
        aliceBlueWS.addSubscriber('marketSentiment_prices', priceCallback);

        // Fetch index prices from REST API as fallback
        fetchIndexPrices();

        // Subscribe to INDEX tokens for live spot prices in header (NIFTY, BANKNIFTY, SENSEX)
        const indexInstruments = Object.values(indicesData).map(idx => ({
          exchange: idx.exchange,
          token: idx.token
        }));
        
        aliceBlueWS.subscribe(indexInstruments);
        console.log('📡 [Market Sentiment] Subscribed to indices:', Object.keys(indicesData).join(', '));
        console.log('📡 Index instruments subscribed:', indexInstruments);

        // Fetch current month futures contracts
        const futures = await fetchCurrentMonthFutures();
        
        // Subscribe to futures contracts for live spot price (used in sentiment cards)
        if (futures && Object.keys(futures).length > 0) {
          const futuresInstruments = Object.values(futures).map(f => ({
            exchange: f.exchange,
            token: f.token
          }));
          
          aliceBlueWS.subscribe(futuresInstruments);
          console.log('📡 [Market Sentiment] Subscribed to futures:', Object.keys(futures).join(', '));
          console.log('📡 Futures instruments subscribed:', futuresInstruments);
          
          // Wait for WebSocket ticks, then reload data
          // We don't call fetchData here since useEffect will trigger once futuresTokens updates
        }

      } catch (error) {
        console.error('❌ [Market Sentiment] WebSocket connection failed:', error);
      }
    };

    initWebSocket();

    // Periodically refresh REST API index prices (every 30s) as fallback
    const indexPriceInterval = setInterval(() => {
      fetchIndexPrices();
    }, 30000);

    return () => {
      mounted = false;
      aliceBlueWS.removeSubscriber('marketSentiment_prices');
      clearInterval(indexPriceInterval);
    };
  }, []);

  // Get live spot price from WebSocket (using futures contract)
  const getLiveSpotPrice = (symbol) => {
    const futuresData = futuresTokens[symbol];
    if (!futuresData) {
      console.log(`⚠️ No futures data for ${symbol}. Available:`, Object.keys(futuresTokens));
      return null;
    }
    
    const key = `${futuresData.exchange}|${futuresData.token}`;
    const priceData = livePrices[key];
    
    if (!priceData?.ltp) {
      console.log(`⚠️ No price data for ${symbol} (${key}). LivePrices keys:`, Object.keys(livePrices).slice(0, 10));
    } else {
      console.log(`✅ Got live price for ${symbol}: ${priceData.ltp}`);
    }
    
    return priceData?.ltp || null;
  };

  // Get live price change from WebSocket (using futures contract)
  const getLivePriceChange = (symbol) => {
    const futuresData = futuresTokens[symbol];
    if (!futuresData) return null;
    
    const key = `${futuresData.exchange}|${futuresData.token}`;
    const priceData = livePrices[key];
    
    return priceData?.changePercent || null;
  };

  // Fetch all data
  const fetchData = async (symbol) => {
    setLoading(true);
    setError(null);

    try {
      // Fetch sentiment, AI insights, and market briefing in parallel
      const [sentimentResponse, aiResponse, briefingResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/india/sentiment?symbol=${symbol}`),
        axios.get(`${API_BASE_URL}/api/india/ai-insight?symbol=${symbol}`),
        axios.get(`${API_BASE_URL}/api/india/market-briefing?symbol=${symbol}`)
      ]);

      if (sentimentResponse.data.success) {
        let sentimentData = sentimentResponse.data.data;
        
        // Override with live WebSocket futures spot price if available
        const liveSpotPrice = getLiveSpotPrice(symbol);
        const livePriceChange = getLivePriceChange(symbol);
        
        if (liveSpotPrice && liveSpotPrice > 0) {
          sentimentData = {
            ...sentimentData,
            spotPrice: liveSpotPrice.toFixed(2),
            spotChange: livePriceChange || 0,
            dataSource: 'live' // Mark as live data from WebSocket
          };
          console.log(`🔴 Using live WebSocket futures price for ${symbol}:`, liveSpotPrice);
        } else {
          console.warn(`⚠️ No live futures price available for ${symbol}`);
        }
        
        setSentimentData(sentimentData);
        
        // Check if data is fallback/unavailable
        if ((sentimentData.fallback || sentimentData.error) && liveSpotPrice) {
          setError('⚠️ NSE option chain data is temporarily unavailable. Spot prices are live from WebSocket. OI data is estimated.');
        } else if (sentimentData.fallback || sentimentData.error) {
          setError('⚠️ NSE data is temporarily unavailable. Showing estimated data.');
        }
      }

      if (aiResponse.data.success) {
        setAiInsights(aiResponse.data.data.insights);
      }

      if (briefingResponse.data.success) {
        setBriefingData(briefingResponse.data.data);
      }
      
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Unable to fetch market data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch (only if futures are loaded) - single useEffect to prevent duplicate calls
  useEffect(() => {
    const futuresCount = Object.keys(futuresTokens).length;
    console.log(`🔍 useEffect triggered - futuresTokens count: ${futuresCount}, selectedSymbol: ${selectedSymbol}`);
    
    if (futuresCount > 0) {
      // Wait a bit for WebSocket to receive at least one tick
      const timer = setTimeout(() => {
        console.log('🔄 Fetching data with futures available...');
        fetchData(selectedSymbol);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol, futuresTokens]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh || Object.keys(futuresTokens).length === 0) return;
    const interval = setInterval(() => {
      fetchData(selectedSymbol);
    }, 180000); // 3 minutes
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedSymbol, futuresTokens]);

  const handleSymbolChange = (symbol) => {
    setSelectedSymbol(symbol);
  };

  const getSentimentColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSentimentBg = (score) => {
    if (score >= 70) return 'bg-green-100';
    if (score >= 40) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const formatNumber = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)}Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)}L`;
    return num.toLocaleString();
  };

  const getRiskColor = (level) => {
    if (level === 'Low') return 'bg-green-500';
    if (level === 'Moderate') return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-4 sm:py-6">
        
        {/* Compact Header */}
        <div className="bg-white rounded-lg shadow-sm p-3 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Market Sentiment</h1>
              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                <FaRobot className="text-purple-600" />
                AI-Powered Analysis {lastUpdated && `• ${lastUpdated.toLocaleTimeString()}`}
                {wsConnected && Object.keys(futuresTokens).length > 0 && (
                  <span className="ml-2 flex items-center gap-1 text-green-600">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                    Live Futures
                  </span>
                )}
              </p>
            </div>
            
            {/* Live Indices Display - Similar to TradingView */}
            <div className="flex items-center gap-4">
              {/* Live Indices Data */}
              <div className="flex items-center gap-2">
                {Object.entries(indicesData).filter(([name]) => ['NIFTY', 'BANKNIFTY', 'SENSEX'].includes(name)).map(([indexName, indexInfo]) => {
                  // Priority 1: Futures WebSocket price (live, updates in real-time)
                  const futData = futuresTokens[indexName];
                  const futKey = futData ? `${futData.exchange}|${futData.token}` : null;
                  const futPriceData = futKey ? livePrices[futKey] : null;
                  
                  // Priority 2: Index token WebSocket price
                  const indexKey = `${indexInfo.exchange}|${indexInfo.token}`;
                  const idxPriceData = livePrices[indexKey];
                  
                  // Priority 3: REST API fallback (static ATM price)
                  const restData = indexPrices[indexName];
                  
                  // Pick the best available price source
                  const wsData = futPriceData?.ltp ? futPriceData : (idxPriceData?.ltp ? idxPriceData : null);
                  const ltp = wsData?.ltp || (restData?.price > 0 ? restData.price : null);
                  const isLive = !!wsData?.ltp;
                  const changeValue = isLive ? (wsData?.change || wsData?.cv || 0) : (restData?.change || 0);
                  const pChangeValue = isLive ? (wsData?.pc || wsData?.changePercent || 0) : (restData?.pChange || 0);
                  
                  return (
                    <div key={indexName} className="flex flex-col items-center px-2 py-1 bg-gray-50 rounded border border-gray-200 min-w-[85px]">
                      <div className="text-[10px] font-semibold text-gray-600 flex items-center gap-1">
                        {indexName}
                        {isLive && <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" title="Live"></span>}
                      </div>
                      <div className="text-xs font-bold text-gray-800">
                        {ltp ? parseFloat(ltp).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '—'}
                      </div>
                      {changeValue !== 0 || pChangeValue !== 0 ? (
                        <div className={`text-[9px] font-medium ${
                          changeValue >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {changeValue >= 0 ? '+' : ''}{parseFloat(changeValue).toFixed(2)}
                          {pChangeValue !== 0 ? ` (${pChangeValue >= 0 ? '+' : ''}${parseFloat(pChangeValue).toFixed(2)}%)` : ''}
                        </div>
                      ) : ltp ? (
                        <div className="text-[9px] font-medium text-gray-400">0.00</div>
                      ) : (
                        <div className="text-[9px] font-medium text-gray-500">—</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Refresh Controls */}
              <div className="flex items-center gap-2 border-l border-gray-300 pl-2">
                <label className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded text-xs border cursor-pointer">
                  <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="w-3 h-3" />
                  Auto
                </label>
                <button onClick={() => fetchData(selectedSymbol)} disabled={loading} className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
                  <FaSync className={loading ? 'animate-spin' : ''} size={12} />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Compact Symbol Selector */}
          <div className="flex gap-2 mt-3">
            {symbols.map((symbol) => (
              <button key={symbol} onClick={() => handleSymbolChange(symbol)} disabled={loading} className={`px-4 py-1.5 rounded font-semibold text-sm transition-all ${selectedSymbol === symbol ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} disabled:opacity-50`}>
                {symbol}
              </button>
            ))}
          </div>
        </div>

        {/* News Ticker */}
        <MarketNewsTicker selectedSymbol={selectedSymbol} onNewsUpdate={handleNewsUpdate} />

        {/* Info banner for outdated analysis */}
        {sentimentData && lastUpdated && (() => {
          const indexInfo = indicesData[selectedSymbol];
          const indexKey = indexInfo ? `${indexInfo.exchange}|${indexInfo.token}` : null;
          const currentLivePrice = indexKey ? livePrices[indexKey]?.ltp : null;
          const backendPrice = parseFloat(sentimentData.spotPrice);
          
          // If live price differs by more than 1% from backend price, show refresh suggestion
          if (currentLivePrice && backendPrice && Math.abs((currentLivePrice - backendPrice) / backendPrice) > 0.01) {
            // Get actual market change from WebSocket (not the difference from backend)
            const wsChange = livePrices[indexKey]?.change || livePrices[indexKey]?.cv;
            const wsChangePercent = livePrices[indexKey]?.changePercent || livePrices[indexKey]?.pc;
            
            // Calculate staleness
            const staleDiff = currentLivePrice - backendPrice;
            const staleDiffPercent = ((currentLivePrice - backendPrice) / backendPrice * 100).toFixed(2);
            
            return (
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-500 p-4 mb-4 rounded-lg shadow-md animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 bg-orange-500 text-white rounded-full p-2">
                    <FaExclamationTriangle className="text-xl" />
                  </div>
                  <div className="flex-1">
                    <p className="text-orange-900 font-bold text-base mb-1">
                      ⚠️ STALE DATA WARNING - Analysis is {Math.abs(staleDiff).toFixed(0)} pts ({Math.abs(staleDiffPercent)}%) behind
                    </p>
                    <p className="text-orange-800 text-sm mb-2">
                      <strong>Current {selectedSymbol}:</strong> ₹{currentLivePrice.toFixed(2)} 
                      {wsChange !== null && wsChange !== undefined && (
                        <span className={`ml-1 font-bold ${wsChange >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          ({wsChange >= 0 ? '+' : ''}{wsChange.toFixed(2)} pts
                          {wsChangePercent !== null && wsChangePercent !== undefined && (
                            <span> / {wsChange >= 0 ? '+' : ''}{wsChangePercent.toFixed(2)}%</span>
                          )}
                          )
                        </span>
                      )}
                      {' | '}
                      <strong>Analysis Based On:</strong> ₹{backendPrice.toFixed(2)} 
                      {' | '}
                      <strong>Time:</strong> {lastUpdated.toLocaleTimeString()}
                    </p>
                    <p className="text-orange-900 text-sm font-semibold">
                      Smart Money levels, support/resistance zones, and AI insights below are OUTDATED! 
                      <button 
                        onClick={() => fetchData(selectedSymbol)} 
                        disabled={loading}
                        className="ml-2 px-4 py-1 bg-orange-600 text-white rounded font-bold hover:bg-orange-700 disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        <FaSync className={loading ? 'animate-spin' : ''} />
                        {loading ? 'Refreshing...' : 'Click Here to Refresh Now'}
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4 rounded">
            <div className="flex items-center gap-2">
              <FaExclamationTriangle className="text-red-500" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {loading && !sentimentData ? (
          <div className="text-center py-12">
            <FaSync className="animate-spin text-blue-600 text-3xl mx-auto mb-2" />
            <p className="text-gray-600">Loading market data...</p>
          </div>
        ) : sentimentData ? (
          <>
            {/* Market Briefing & Confidence - Top Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              
              {/* Market Briefing */}
              {briefingData?.briefing && (
                <div className="lg:col-span-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <FaBrain className="text-blue-600 text-lg" />
                    <h3 className="font-bold text-gray-800">Market Briefing</h3>
                    <span className={`ml-auto px-2 py-0.5 ${getRiskColor(briefingData.briefing.riskLevel)} text-white text-xs rounded-full font-semibold`}>
                      {briefingData.briefing.riskLevel} Risk
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2 leading-relaxed">{briefingData.briefing.briefingSummary}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white rounded p-2">
                      <p className="text-gray-500 font-medium mb-1">Approach</p>
                      <p className="text-gray-800">{briefingData.briefing.tradeApproach}</p>
                    </div>
                    <div className="bg-white rounded p-2">
                      <p className="text-gray-500 font-medium mb-1">Avoid</p>
                      <p className="text-gray-800">{briefingData.briefing.thingsToAvoid}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Confidence Score */}
              {briefingData?.confidence && (
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg shadow-sm p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <FaShieldAlt className="text-purple-600 text-lg" />
                    <h3 className="font-bold text-gray-800">Confidence</h3>
                  </div>
                  <div className="text-center mb-3">
                    <div className="text-4xl font-bold text-purple-600">{briefingData.confidence.confidenceScore}</div>
                    <div className="text-sm font-semibold text-purple-800">{briefingData.confidence.confidenceLabel}</div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div className="bg-purple-600 h-2 rounded-full transition-all" style={{width: `${briefingData.confidence.confidenceScore}%`}}></div>
                  </div>
                  <p className="text-xs text-gray-700">{briefingData.confidence.tradingEnvironment}</p>
                </div>
              )}
            </div>

            {/* Sentiment Gauge & Indicators - Compact */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
              
              {/* Sentiment Score */}
              <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                <h3 className="text-xs font-semibold text-gray-500 mb-2">SENTIMENT SCORE</h3>
                <div className="flex items-center justify-center">
                  <div className="relative w-24 h-24">
                    <svg viewBox="0 0 100 100" className="transform -rotate-90">
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                      <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="8" strokeDasharray={`${sentimentData.sentimentScore * 2.827} 283`} strokeLinecap="round" className={getSentimentColor(sentimentData.sentimentScore)} />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className={`text-2xl font-bold ${getSentimentColor(sentimentData.sentimentScore)}`}>
                        {sentimentData.sentimentScore}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-center text-xs text-gray-600 mt-2">{sentimentData.sentimentLabel}</p>
              </div>

              <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 relative">
                  {(() => {
                    // Get live spot price from WebSocket based on selected symbol
                    const indexInfo = indicesData[selectedSymbol];
                    const indexKey = indexInfo ? `${indexInfo.exchange}|${indexInfo.token}` : null;
                    const livePrice = indexKey ? livePrices[indexKey] : null;
                    const spotPrice = livePrice?.ltp || sentimentData.spotPrice;
                    const changeValue = livePrice?.change || livePrice?.cv;
                    const hasLiveData = livePrice?.ltp !== undefined;
                    
                    return (
                      <>
                        {hasLiveData && (
                          <div className="absolute top-1 right-1 flex items-center gap-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-xs text-green-600 font-semibold">LIVE</span>
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mb-1">Spot</p>
                        <p className="text-lg font-bold text-gray-800">₹{formatNumber(spotPrice)}</p>
                        {changeValue !== null && changeValue !== undefined ? (
                          <p className={`text-xs font-semibold ${changeValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {changeValue >= 0 ? '▲' : '▼'} {Math.abs(changeValue).toFixed(2)}
                          </p>
                        ) : (
                          <p className={`text-xs font-semibold ${sentimentData.spotChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {sentimentData.spotChange >= 0 ? '▲' : '▼'} {Math.abs(sentimentData.spotChange).toFixed(2)}%
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="bg-white rounded-lg shadow-sm p-3 border border-purple-200">
                  <p className="text-xs text-gray-500 mb-1">VIX</p>
                  <p className="text-lg font-bold text-purple-700">{sentimentData.vix.toFixed(2)}</p>
                  <p className={`text-xs font-semibold ${sentimentData.vixChange >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {sentimentData.vixChange >= 0 ? '▲' : '▼'} {Math.abs(sentimentData.vixChange).toFixed(2)}%
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-3 border border-blue-200">
                  <p className="text-xs text-gray-500 mb-1">PCR</p>
                  <p className="text-lg font-bold text-blue-700">{sentimentData.pcr.toFixed(2)}</p>
                  <p className="text-xs text-gray-600">{sentimentData.pcr > 1.2 ? '🔥 Bullish' : sentimentData.pcr < 0.8 ? '❄️ Bearish' : '⚖️ Neutral'}</p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-3 border border-green-200">
                  <p className="text-xs text-gray-500 mb-1">A/D</p>
                  <p className="text-lg font-bold text-green-700">{sentimentData.advanceDeclineRatio.toFixed(2)}</p>
                  <p className="text-xs text-gray-600">A:{sentimentData.advances} D:{sentimentData.declines}</p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-3 border border-red-200">
                  <p className="text-xs text-gray-500 mb-1">CE OI</p>
                  <p className="text-sm font-bold text-red-700">{formatNumber(sentimentData.totalCEOI)}</p>
                  <p className={`text-xs font-semibold ${sentimentData.changeCEOI >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {sentimentData.changeCEOI >= 0 ? '▲' : '▼'} {formatNumber(Math.abs(sentimentData.changeCEOI))}
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-3 border border-green-200">
                  <p className="text-xs text-gray-500 mb-1">PE OI</p>
                  <p className="text-sm font-bold text-green-700">{formatNumber(sentimentData.totalPEOI)}</p>
                  <p className={`text-xs font-semibold ${sentimentData.changePEOI >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {sentimentData.changePEOI >= 0 ? '▲' : '▼'} {formatNumber(Math.abs(sentimentData.changePEOI))}
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-3 border border-orange-200">
                  <p className="text-xs text-gray-500 mb-1">CE Δ</p>
                  <p className={`text-sm font-bold ${sentimentData.changeCEOI >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {sentimentData.changeCEOI >= 0 ? '+' : ''}{formatNumber(sentimentData.changeCEOI)}
                  </p>
                  <p className="text-xs text-gray-600">Call activity</p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-3 border border-teal-200">
                  <p className="text-xs text-gray-500 mb-1">PE Δ</p>
                  <p className={`text-sm font-bold ${sentimentData.changePEOI >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {sentimentData.changePEOI >= 0 ? '+' : ''}{formatNumber(sentimentData.changePEOI)}
                  </p>
                  <p className="text-xs text-gray-600">Put activity</p>
                </div>
              </div>
            </div>

            {/* Smart Money & No-Trade Zone */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              
              {/* Smart Money Analysis */}
              {briefingData?.smartMoney && (() => {
                const indexInfo = indicesData[selectedSymbol];
                const indexKey = indexInfo ? `${indexInfo.exchange}|${indexInfo.token}` : null;
                const currentLivePrice = indexKey ? livePrices[indexKey]?.ltp : null;
                const backendPrice = parseFloat(sentimentData.spotPrice);
                const isStale = currentLivePrice && backendPrice && Math.abs((currentLivePrice - backendPrice) / backendPrice) > 0.01;
                
                return (
                  <div className={`bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-sm p-4 border border-green-200 relative ${isStale ? 'opacity-75' : ''}`}>
                    {isStale && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse z-10">
                        OUTDATED
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <FaChartLine className="text-green-600 text-lg" />
                      <h3 className="font-bold text-gray-800">Smart Money</h3>
                      <span className={`ml-auto px-2 py-0.5 text-xs rounded-full font-semibold ${briefingData.smartMoney.smartMoneyBias === 'Bullish' ? 'bg-green-500 text-white' : briefingData.smartMoney.smartMoneyBias === 'Bearish' ? 'bg-red-500 text-white' : 'bg-gray-500 text-white'}`}>
                        {briefingData.smartMoney.smartMoneyBias}
                      </span>
                    </div>
                    {lastUpdated && (
                      <p className="text-[10px] text-gray-500 mb-2 italic">
                        ⏱ Analysis from: {lastUpdated.toLocaleTimeString()} 
                        {currentLivePrice && (
                          <span className={isStale ? 'text-red-600 font-bold' : ''}>
                            {' | Current Live: ₹'}{currentLivePrice.toFixed(2)}
                          </span>
                        )}
                      </p>
                    )}
                    <p className="text-sm text-gray-700 mb-3">{briefingData.smartMoney.explanation}</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-gray-500 mb-1">Support</p>
                        <p className="font-semibold text-gray-800">{briefingData.smartMoney.supportZone}</p>
                      </div>
                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-gray-500 mb-1">Structure</p>
                        <p className="font-semibold text-gray-800">{briefingData.smartMoney.marketStructure}</p>
                      </div>
                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-gray-500 mb-1">Resistance</p>
                        <p className="font-semibold text-gray-800">{briefingData.smartMoney.resistanceZone}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* No-Trade Zone */}
              {briefingData?.noTradeZone && (
                <div className={`rounded-lg shadow-sm p-4 border ${briefingData.noTradeZone.isNoTradeZone ? 'bg-gradient-to-br from-red-50 to-orange-50 border-red-200' : 'bg-gradient-to-br from-green-50 to-teal-50 border-green-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <FaShieldAlt className={briefingData.noTradeZone.isNoTradeZone ? 'text-red-600' : 'text-green-600'} />
                    <h3 className="font-bold text-gray-800">Trade Safety</h3>
                    <span className={`ml-auto px-2 py-0.5 text-xs rounded-full font-semibold ${briefingData.noTradeZone.isNoTradeZone ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
                      {briefingData.noTradeZone.isNoTradeZone ? 'NO TRADE' : 'SAFE'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800 font-semibold mb-2">{briefingData.noTradeZone.reason}</p>
                  <p className="text-xs text-gray-700">{briefingData.noTradeZone.riskSummary}</p>
                </div>
              )}
            </div>

            {/* Movement Explanation */}
            {briefingData?.movement && (
              <div className="bg-white rounded-lg shadow-sm p-4 mb-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <FaLightbulb className="text-yellow-600 text-lg" />
                  <h3 className="font-bold text-gray-800">Why Market Moved</h3>
                </div>
                <p className="text-sm text-gray-800 font-semibold mb-2">{briefingData.movement.primaryReason}</p>
                {briefingData.movement.supportingFactors.length > 0 && (
                  <ul className="text-xs text-gray-700 space-y-1 mb-2">
                    {briefingData.movement.supportingFactors.map((factor, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span className="text-blue-600 mt-0.5">•</span>
                        <span>{factor}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">{briefingData.movement.marketBehaviorSummary}</p>
              </div>
            )}

            {/* AI Insights - Compact */}
            {aiInsights && (() => {
              const indexInfo = indicesData[selectedSymbol];
              const indexKey = indexInfo ? `${indexInfo.exchange}|${indexInfo.token}` : null;
              const currentLivePrice = indexKey ? livePrices[indexKey]?.ltp : null;
              const backendPrice = parseFloat(sentimentData.spotPrice);
              const isStale = currentLivePrice && backendPrice && Math.abs((currentLivePrice - backendPrice) / backendPrice) > 0.01;
              
              return (
                <div className={`bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shadow-sm p-4 mb-4 border border-purple-200 relative ${isStale ? 'opacity-75' : ''}`}>
                  {isStale && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse z-10">
                      OUTDATED
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <FaRobot className="text-purple-600 text-lg" />
                    <h2 className="text-lg font-bold text-gray-800">AI Insights</h2>
                  </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                  <div className="bg-white rounded p-2 text-center border border-purple-100">
                    <p className="text-xs text-gray-500 mb-1">Sentiment</p>
                    <p className="text-sm font-bold text-gray-800">{aiInsights.marketSentiment}</p>
                  </div>
                  <div className="bg-white rounded p-2 text-center border border-blue-100">
                    <p className="text-xs text-gray-500 mb-1">Trend</p>
                    <p className="text-sm font-bold text-gray-800">{aiInsights.trend}</p>
                  </div>
                  <div className="bg-white rounded p-2 text-center border border-green-100">
                    <p className="text-xs text-gray-500 mb-1">Strength</p>
                    <p className="text-sm font-bold text-gray-800">{aiInsights.marketStrength}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="bg-white rounded p-3 text-sm border border-blue-100">
                    <p className="font-semibold text-blue-700 mb-1 flex items-center gap-1">
                      <span>📊</span> Volatility
                    </p>
                    <p className="text-gray-700 text-xs">{aiInsights.volatilityView}</p>
                  </div>

                  <div className="bg-white rounded p-3 text-sm border border-purple-100">
                    <p className="font-semibold text-purple-700 mb-1 flex items-center gap-1">
                      <span>🎯</span> Options
                    </p>
                    <p className="text-gray-700 text-xs">{aiInsights.optionsInsight}</p>
                  </div>

                  <div className="bg-white rounded p-3 text-sm border border-green-100">
                    <p className="font-semibold text-green-700 mb-1 flex items-center gap-1">
                      <span>📈</span> Breadth
                    </p>
                    <p className="text-gray-700 text-xs">{aiInsights.breadthInsight}</p>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded p-3 border border-blue-200">
                    <p className="font-semibold text-blue-800 mb-1 flex items-center gap-1">
                      <span>💡</span> Conclusion
                    </p>
                    <p className="text-gray-800 text-sm font-medium">{aiInsights.aiConclusion}</p>
                    {newsSentiment && (
                      <p className="text-xs text-gray-700 mt-2 pt-2 border-t border-blue-200">
                        <span className="font-semibold">News Flow: </span>
                        {newsSentiment.bias === 'Positive' && <span className="text-green-600">Supportive (positive bias)</span>}
                        {newsSentiment.bias === 'Negative' && <span className="text-red-600">Risky (negative headlines)</span>}
                        {newsSentiment.bias === 'Neutral' && <span className="text-gray-600">Neutral (mixed)</span>}
                      </p>
                    )}
                  </div>

                  <div className="bg-white rounded p-3 border border-yellow-200">
                    <p className="font-semibold text-yellow-700 mb-2 flex items-center gap-1 text-sm">
                      <span>🎲</span> Trade Bias
                    </p>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded font-bold text-sm ${aiInsights.tradeBias === 'Long' ? 'bg-green-500 text-white' : aiInsights.tradeBias === 'Short' ? 'bg-red-500 text-white' : 'bg-gray-500 text-white'}`}>
                        {aiInsights.tradeBias}
                      </span>
                      <p className="text-xs text-gray-600">Based on current conditions</p>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded p-3">
                    <p className="font-semibold text-yellow-800 mb-1 flex items-center gap-1 text-sm">
                      <FaExclamationTriangle className="text-yellow-600" />
                      Risk Notes
                    </p>
                    <p className="text-yellow-900 text-xs">{aiInsights.riskNotes}</p>
                  </div>
                </div>
              </div>
            );
            })()}

            {/* Disclaimer - Compact */}
            <div className="bg-red-50 border-l-4 border-red-500 rounded p-3">
              <div className="flex items-start gap-2">
                <FaExclamationTriangle className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-red-800 text-sm mb-1">⚠️ Educational Purpose Only</h3>
                  <p className="text-xs text-red-900 leading-relaxed">
                    This analysis is for educational purposes only and does not constitute financial advice. Trading involves substantial risk. Conduct your own research and consult a qualified advisor.
                  </p>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default MarketSentimentPage;
